import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Sheet, useCloseGuard, useModalA11y } from "@cntrl/ui";
import { usePhoneLayout } from "../../app/phoneLayout.js";
import styles from "./CalendarEventDetails.module.scss";

/**
 * The Details SHELL (ART-305 / ART-327 / ART-328): a pop-up on desktop, anchored to what opened it (centred when
 * nothing did), and a bottom sheet on the phone — the same content in both. Shared by the calendar's event details
 * and the show request's, so the two cannot drift in how they open, dim or dismiss.
 */
export function DetailsFrame({ title, anchor, onClose, children, wide, dirty = false }: {
  title: string;
  anchor: DOMRect | null;
  onClose: () => void;
  children: ReactNode;
  /** The show request's wider pop-up (it carries terms, a lineup and a calendar strip). */
  wide?: boolean;
  /** Content holds something not yet sent (Who's playing, ART-342): every way out asks "Discard changes?" first. */
  dirty?: boolean;
}) {
  const isMobile = usePhoneLayout();
  if (isMobile) {
    return (
      <Sheet title={title} onClose={onClose} scroll="inner" className={styles.sheet} dirty={dirty}>
        <div className={styles.frame} data-shell="mobile">{children}</div>
      </Sheet>
    );
  }
  return (
    <AnchoredPopover anchor={anchor} title={title} onClose={onClose} wide={wide} dirty={dirty}>
      <div className={styles.frame} data-shell="desktop">{children}</div>
    </AnchoredPopover>
  );
}

/** The desktop pop-up: anchored to the clicked element — right of it +10px, flipped left when that overflows,
 *  centred when neither fits — its top ~16px above the anchor and clamped 16px inside the viewport. Portalled to
 *  <body> (the containing-block rule for overlays), over a transparent full-page catcher that closes it. */
function AnchoredPopover({ anchor, title, onClose, children, wide, dirty }: {
  anchor: DOMRect | null; title: string; onClose: () => void; children: ReactNode; wide?: boolean; dirty: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // The one exit (CloseGuard): Escape, the catcher and any in-content ✕ / Cancel all ask before discarding.
  const guard = useCloseGuard({ onClose, dirty, root: ref });
  const dismiss = () => void guard.requestClose();
  useModalA11y(ref, dismiss, true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const gap = 10;
      const edge = 16;
      let left: number;
      if (!anchor) left = (vw - w) / 2;
      else if (anchor.right + gap + w <= vw - edge) left = anchor.right + gap;
      else if (anchor.left - gap - w >= edge) left = anchor.left - gap - w;
      else left = (vw - w) / 2;
      const wanted = anchor ? anchor.top - 16 : (vh - h) / 2;
      const top = Math.max(edge, Math.min(wanted, vh - edge - h));
      setPos({ left: Math.max(12, left), top });
    };
    place();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    ro?.observe(el);
    window.addEventListener("resize", place);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  const node = (
    <div className={styles.popLayer}>
      <button type="button" className={styles.catcher} aria-label="Close" tabIndex={-1} onClick={dismiss} />
      <div
        ref={ref}
        className={styles.popover}
        data-wide={wide || undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={pos ? { left: pos.left, top: pos.top } : { visibility: "hidden" }}
      >
        {guard.provide(children)}
      </div>
      {guard.confirm}
    </div>
  );
  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
}

